import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api/ai-smart-button-store';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { client } from '@atlassian/rovo-content-bridge-api';
import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';
import {
	InlineInputBoxSendMessageCommand,
	ROVO_CHAT_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';
import {
	type ChatAction,
	type ConversationAction,
	HandleActionType,
	usePromptCards,
} from '@atlassian/rovo-conversation-actions';

import { applyOnboardingRovoConfig } from '@confluence/experiment-rovo-onboarding-twcg-1387/entry-points/applyOnboardingRovoConfig.ts';
import { useOnboardingEntryPointPublish } from '@confluence/experiment-rovo-onboarding-twcg-1387/entry-points/useOnboardingEntryPointPublish.ts';
import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useNudgeContributorFilter } from '@confluence/page-rovo-config-common/entry-points/useNudgeContributorFilter.ts';
import { useNudgeFirstVisitFilter } from '@confluence/page-rovo-config-common/entry-points/useNudgeFirstVisitFilter.ts';
import { TASK_ITEM_NODE_TYPE } from '@confluence/page-rovo-config-common/entry-points/useAgentMentionNudgeAction.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import {
	type RovoConfiguration,
	type RovoConfigurationProviderProps,
	type PageRovoConfigProviderProps,
	isPageRovoConfigProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useContentReviewerAgent } from './hooks/useContentReviewerAgent.ts';
import { useLiveDocNudgeRecommendationFilter } from './hooks/useLiveDocNudgeRecommendationFilter.ts';
import { useEditPageNudgeConfig } from './hooks/useEditPageNudgeConfig.ts';
import {
	useEditorRovoButtonActionsActions,
	useEditorRovoButtonActionsContext,
	useEditorRovoButtonAgentMentionId,
	useEditorRovoButtonAgentMentionInsertionCount,
	useEditorRovoButtonResolvedAgentMentionId,
	useEditorRovoButtonAgentMentionContext,
	useEditorRovoButtonAgentMentionPrompt,
	useEditorRovoButtonAgentMentionName,
	useEditorRovoButtonAgentMentionParentNodeType,
	useEditorRovoButtonAgentMentionNodeLocalId,
} from './store/EditorRovoButtonActionsStore.tsx';
import { getEmptyPageCreationActions } from './utils/getEmptyPageCreationActions.tsx';
import emptyPageGreetingIcon from './assets/empty-page-greeting-icon.svg';

const messages = defineMessages({
	emptyPageGreetingTitle: {
		id: 'edit-page-rovo-config-provider.empty-page-greeting-title',
		defaultMessage: 'Start your doc with a prompt',
		description: 'Greeting title shown in the Rovo empty state when the editor page is empty',
	},
	copiedPageGreetingTitle: {
		id: 'edit-page-rovo-config-provider.copied-page-greeting-title',
		defaultMessage: 'Need help editing your copied page?',
		description:
			'Greeting title shown in the Rovo chat sidebar when the user is editing a page that was recently copied',
	},
	suggestEditsMenuItemLabel: {
		id: 'edit-page-rovo-config-provider.menu.item.label.suggest-edits',
		defaultMessage: 'Review with suggestions',
		description:
			'Label for the Suggest Edits action in the Rovo Chat actions list while editing a page. Clicking this starts a chat with the OOTB Content Reviewer agent to review the page content and suggest improvements.',
	},
	suggestEditsMenuItemPrompt: {
		id: 'edit-page-rovo-config-provider.menu.item.prompt.suggest-edits-agent',
		defaultMessage: 'Review my content and suggest improvements',
		description:
			'Prompt sent to Rovo Chat when a user clicks the "Review with suggestions" action while editing a page. It asks the Content Reviewer agent to review the page content and suggest improvements.',
	},
});

/** Max length for the prompt-derived chat history title (matches other Rovo chat channels). */
const AGENT_MENTION_CHAT_TITLE_MAX_LENGTH = 30;
const AGENT_MENTION_BRIDGE_CLIENT_PREFIX = 'confluence-editor-agent-mention-';

const EditPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	// Temporary: opens Rovo mini-modal for Founding Admins landing after onboarding.
	// Remove when experiment twcg-1387-rovo-in-onboarding-chat is cleaned up.
	const onboardingConfig = useOnboardingEntryPointPublish();

	const viewPageRovoActions = useViewPageRovoActions();
	const editorActions = useEditorRovoButtonActionsActions();
	const editorActionsContext = useEditorRovoButtonActionsContext();
	const rovoConfigProviderProps = useRovoConfigProviderProps();
	const { isRovoLLMEnabled } = useSessionData();
	// Resolve the OOTB Content Reviewer agent that backs the "Review with suggestions" entry point.
	// Existence check only (no provisioning); if the agent isn't provisioned the entry point is
	// hidden.
	const contentReviewerAgent = useContentReviewerAgent();
	const isEditorEmpty =
		isPageRovoConfigProviderProps(rovoConfigProviderProps) && rovoConfigProviderProps.isEditorEmpty;
	const { formatMessage } = useIntl();

	const { promptCards } = usePromptCards();

	const isEmptyPageWithPromptCards = isEditorEmpty && Array.isArray(promptCards);

	const pageProps = isPageRovoConfigProviderProps(rovoConfigProviderProps)
		? rovoConfigProviderProps
		: null;

	const contentType = pageProps?.contentType ?? '';
	const contentId = pageProps?.contentId ?? '';
	const readTimeMinutes = pageProps?.readTime ?? null;
	const readTimeLoading = pageProps?.readTimeLoading ?? false;
	const suggestEditsActionIcon = useCallback(() => <TextReviewIcon label="" />, []);

	const enableLiveDocNudges = useLiveDocNudgeRecommendationFilter(contentType);

	const { eligibleForChangeboarding } = useAudioButtonWrapperStore();
	const { isFirstVisit, loading: isFirstVisitLoading } = useNudgeFirstVisitFilter(
		contentId,
		!enableLiveDocNudges,
	);
	const { isCurrentUserContributor, loading: isContributorLoading } = useNudgeContributorFilter(
		contentId,
		!enableLiveDocNudges,
	);

	const isEligibilityLoading = isFirstVisitLoading || isContributorLoading || readTimeLoading;

	const agentMentionId = useEditorRovoButtonAgentMentionId();
	const agentMentionInsertionCount = useEditorRovoButtonAgentMentionInsertionCount();
	const resolvedAgentMentionId = useEditorRovoButtonResolvedAgentMentionId();
	const agentMentionContext = useEditorRovoButtonAgentMentionContext();
	const agentMentionPrompt = useEditorRovoButtonAgentMentionPrompt();
	const agentMentionName = useEditorRovoButtonAgentMentionName();
	const agentMentionParentNodeType = useEditorRovoButtonAgentMentionParentNodeType();
	const agentMentionNodeLocalId = useEditorRovoButtonAgentMentionNodeLocalId();
	const isTaskItemMention = agentMentionParentNodeType === TASK_ITEM_NODE_TYPE;

	const nudges = useEditPageNudgeConfig(
		enableLiveDocNudges,
		eligibleForChangeboarding,
		isFirstVisit,
		isCurrentUserContributor,
		isEligibilityLoading,
		readTimeMinutes,
		// Task items use auto-fire instead; expValEquals last ensures exposure is tracked.
		!isTaskItemMention && expValEquals('platform_editor_agent_mentions', 'isEnabled', true)
			? agentMentionId
			: null,
		resolvedAgentMentionId,
		agentMentionContext,
		agentMentionPrompt,
		agentMentionParentNodeType,
		agentMentionInsertionCount,
		agentMentionName,
		agentMentionNodeLocalId,
	);
	const { setNudges } = useAISmartButtonActions();
	const publish = usePublish('ai-mate');
	const agentMentionBridgeClientId = useMemo(
		() => client(`${AGENT_MENTION_BRIDGE_CLIENT_PREFIX}${contentId || 'fallback'}`),
		[contentId],
	);
	const { send: sendBridgeMessage, ready: readyBridgeClient } = useRovoContentBridgeAPI(
		agentMentionBridgeClientId,
	);
	const hasCalledBridgeReadyRef = useRef(false);

	// Keep refs so the auto-fire effect can read the latest agent mention prompt/context without
	// them being reactive dependencies. Adding them to the dep array would cause the effect to
	// re-fire on every keystroke in the task item without agentMentionInsertionCount changing.
	const agentMentionPromptRef = useRef(agentMentionPrompt);
	agentMentionPromptRef.current = agentMentionPrompt;
	const agentMentionContextRef = useRef(agentMentionContext);
	agentMentionContextRef.current = agentMentionContext;
	// Keep the invoking node localId in a ref so the auto-fire effect can read the latest value
	// without it being a reactive dependency (matching the prompt/context refs above).
	const agentMentionNodeLocalIdRef = useRef(agentMentionNodeLocalId);
	agentMentionNodeLocalIdRef.current = agentMentionNodeLocalId;
	// Auto-fire Rovo when an agent is @mentioned inside a task item.
	// The inline-Rovo treatment runs through the content bridge without opening chat; the control
	// retains the existing mini-modal flow. Both remain gated by platform_editor_agent_mentions.
	useEffect(() => {
		if (
			!isTaskItemMention ||
			!resolvedAgentMentionId ||
			!agentMentionInsertionCount ||
			!expValEquals('platform_editor_agent_mentions', 'isEnabled', true)
		) {
			return;
		}

		const promptText = agentMentionPromptRef.current?.trim();
		if (expValEquals('platform_editor_agent_mentions_nudge_indication', 'isEnabled', true)) {
			if (!contentId || !promptText) {
				return;
			}

			if (!hasCalledBridgeReadyRef.current) {
				readyBridgeClient();
				hasCalledBridgeReadyRef.current = true;
			}

			sendBridgeMessage(InlineInputBoxSendMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
				prompt: promptText,
				source: 'agentMentionTaskItemAutoFire',
				productKey: 'confluence',
				contentId,
				agentId: resolvedAgentMentionId,
				...(agentMentionName ? { agentName: agentMentionName } : {}),
				...(agentMentionId
					? {
							agentIdentityAccountId: IdentityUserAri.create({
								userId: agentMentionId,
							}).toString(),
						}
					: {}),
				deepResearchEnabled: false,
				webSearchEnabled: false,
				openChat: false,
				openChatOnError: false,
				addCurrentPageAsContextUserPreference: true,
				contentContext: 'global',
			});
			return;
		}

		const prompt = agentMentionContextRef.current ?? agentMentionPromptRef.current;
		// Plain-text title for the chat history entry, derived from the task text. Inline-atom
		// nodes (the agent mention, custom/reaction emoji, etc.) are dropped by ProseMirror's
		// textContent; plain typed text is kept. Truncate by code point rather than UTF-16 unit
		// so an astral character (e.g. a typed Unicode emoji) on the boundary isn't split into an
		// unpaired surrogate. Without a title the auto-sent, seeded conversation shows only a date.
		const conversationName = promptText
			? Array.from(promptText).slice(0, AGENT_MENTION_CHAT_TITLE_MAX_LENGTH).join('')
			: undefined;

		publish({
			type: 'chat-new',
			source: 'agentMentionTaskItemAutoFire',
			openChatMode: 'mini-modal',
			autoFocusInput: false,
			data: {
				...(prompt ? { prompt } : {}),
				...(conversationName && fg('platform_editor_agent_mentions_drop_one_fixes')
					? { name: conversationName }
					: {}),
				isPromptPlaceholder: false,
				dialogues: [],
				mode: { useCurrentPageContext: true },
				agentId: resolvedAgentMentionId,
				...(agentMentionName ? { agentName: agentMentionName } : {}),
				// Reconstruct the full identity ARI from the bare accountId stored in the ADF mention node.
				// agentMentionId is "712020:..." (bare), but downstream consumers (TelepointerAvatar,
				// sendMessageActionProvider) expect the full "ari:cloud:identity::user/712020:..." ARI.
				...(agentMentionId
					? {
							agentIdentityAccountId: IdentityUserAri.create({ userId: agentMentionId }).toString(),
						}
					: {}),
				// Seed the agent-run-state bridge with the invoking node, behind its experiment.
				...(agentMentionNodeLocalIdRef.current &&
				expValEquals('platform_editor_agent_state_bridge', 'isEnabled', true)
					? { invokedByNodeLocalId: agentMentionNodeLocalIdRef.current }
					: {}),
			},
		});
	}, [
		agentMentionId,
		agentMentionInsertionCount,
		agentMentionName,
		contentId,
		readyBridgeClient,
		resolvedAgentMentionId,
		sendBridgeMessage,
		isTaskItemMention,
		// agentMentionPrompt/agentMentionContext intentionally omitted — read via refs above to avoid
		// re-firing on every keystroke after the mention is inserted.
		publish,
	]);

	const config = useMemo<RovoConfiguration>(() => {
		// A recently-copied page in the `rovoChat` cohort injects the "Remove content, keep structure"
		// action into the editor-actions store (via CopyWithRovo → setActions) without a selection
		// `context.reference`. CopyWithRovo only injects it for a page that actually came through the
		// copy flow (`?copiedPage=<sourceId>`), and the selection-based editor-actions flow always sets
		// `reference`, so `rovoChat cohort + editor actions present + no reference` identifies the
		// copied-page injection. In that case we pin the injected action(s) as the sole, prioritized
		// actions so the copy-with-rovo action is the top option rather than being interleaved with /
		// buried under the prompt-of-day and dynamic conversation starters downstream. This is also the
		// single signal behind the copied-page greeting below — do not gate that on the cohort alone,
		// or every edit page in the cohort shows the copied-page title.
		// Evaluate the experiment last so exposure is only tracked once the copied-page signal
		// (injected editor actions, no selection reference) is present.
		const isCopiedPageRovoChat =
			editorActions.length > 0 &&
			!editorActionsContext?.reference &&
			expValEquals('cc_fd_copy_with_rovo', 'cohort', 'rovoChat');

		const getCustomGreeting = () => {
			if (isEmptyPageWithPromptCards && !isCopiedPageRovoChat) {
				return {
					iconSrc: emptyPageGreetingIcon,
					title: formatMessage(messages.emptyPageGreetingTitle),
				};
			}
			if (isCopiedPageRovoChat) {
				return { title: formatMessage(messages.copiedPageGreetingTitle) };
			}
			return undefined;
		};

		const customGreeting = getCustomGreeting();

		const suggestEditsAction: ChatAction = {
			id: 'suggest-edits-ai-action',
			title: formatMessage(messages.suggestEditsMenuItemLabel),
			icon: suggestEditsActionIcon,
			handleType: HandleActionType.Chat,
			staticPrompt: formatMessage(messages.suggestEditsMenuItemPrompt),
			agent: contentReviewerAgent,
		};

		// Show the suggest-edits action (chat with the OOTB Content Reviewer agent) only when the
		// gate is on and the agent is provisioned — no skill fallback, no provisioning on click.
		// The experiment is evaluated in its own statement (not chained with the gate) to satisfy
		// the no-preconditioning rule; the agent is only resolved when the gate is on.
		const isSuggestEditsExperimentEnabled =
			isRovoLLMEnabled &&
			!!contentReviewerAgent &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true);
		const isSuggestEditsActionEnabled =
			isSuggestEditsExperimentEnabled && fg('content_reviewer_agent');
		const combinedEditorActions = isSuggestEditsActionEnabled
			? [suggestEditsAction, ...editorActions]
			: editorActions;

		const baseConfig: RovoConfiguration = {
			getActions: async () => {
				// Copied-page rovoChat cohort: return only the injected editor action(s) so
				// copy-with-rovo ("Remove content, keep structure") is the top option. Use
				// `editorActions` (not `combinedEditorActions`) so the optional suggest-edits action
				// cannot sit above it. Paired with `prioritizeCustomActionResults` below.
				if (isCopiedPageRovoChat) {
					return Promise.resolve(editorActions);
				}

				if (isEmptyPageWithPromptCards) {
					return Promise.resolve(getEmptyPageCreationActions(promptCards));
				}

				if (editorActionsContext?.reference) {
					return Promise.resolve(combinedEditorActions);
				}

				const viewPageActions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);

				return Promise.resolve([...combinedEditorActions, ...viewPageActions]);
			},
			customGreeting,
			shouldShowGreeting: customGreeting !== undefined || undefined,
			prioritizeCustomActionResults:
				isCopiedPageRovoChat || isEmptyPageWithPromptCards ? true : undefined,
		};
		return applyOnboardingRovoConfig(baseConfig, onboardingConfig);
	}, [
		editorActions,
		editorActionsContext,
		viewPageRovoActions,
		isEmptyPageWithPromptCards,
		isRovoLLMEnabled,
		contentReviewerAgent,
		promptCards,
		formatMessage,
		suggestEditsActionIcon,
		onboardingConfig,
	]);

	useEffect(() => {
		setConfiguration(config);
		setNudges(nudges);
	}, [config, setConfiguration, nudges, setNudges]);

	return null;
};

export const EditPageRovoConfigProvider = (
	props: RovoConfigurationProviderProps,
): React.JSX.Element => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	return (
		<CommentCountsProvider contentId={contentId}>
			<EditPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
